/**
 * Events list — append-only domain event log (read-only generic table).
 *
 * The dedicated, filterable Event Stream lives at `/events` (frontend-display
 * §9.1); this generic table is the tooling/test mirror at `/events/table`. It
 * carries the **Emit event** affordance (POST /api/events, `11 §3.1`) — the one
 * write a human can make against the kernel log — alongside per-row open.
 * Columns: event_type, artifact_id, source, status, created_at.
 */
import * as React from "react";
import { useMemo } from "react";
import { useNavigation } from "@refinedev/core";
import type { ColumnDef } from "@tanstack/react-table";
import type { Event } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { PageHeader } from "@/components/layout/PageHeader";
import { RefLink } from "@/components/inspector";
import { StatusBadge } from "@/components/app/StatusBadge";
import { RunsDataTable } from "../_shared/RunsDataTable";
import { formatDateTime, orDash } from "../_shared/runs-ui";
import { EmitEventDialog } from "./EmitEventDialog";

export function EventList() {
  const { show } = useNavigation();
  const [emitOpen, setEmitOpen] = React.useState(false);

  const columns = useMemo<ColumnDef<Event>[]>(
    () => [
      {
        id: "event_type",
        accessorKey: "event_type",
        header: "Type",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.event_type}</span>
        ),
      },
      {
        id: "artifact_id",
        accessorKey: "artifact_id",
        header: "Artifact",
        enableSorting: false,
        cell: ({ row }) => (
          <span onClick={(e) => e.stopPropagation()}>
            <RefLink resource="artifacts" id={row.original.artifact_id} />
          </span>
        ),
      },
      {
        id: "source",
        accessorKey: "source",
        header: "Source",
        cell: ({ row }) => orDash(row.original.source),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatDateTime(row.original.created_at)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Events"
        subtitle="Append-only domain event log. Every state mutation emits an event."
        icon="events"
        actions={
          <Button
            size="sm"
            onClick={() => setEmitOpen(true)}
            data-testid="event-emit"
          >
            <Icon name="create" size={16} className="mr-2" aria-hidden /> Emit
            event
          </Button>
        }
      />
      <RunsDataTable<Event>
        resource="events"
        columns={columns}
        onRowClick={(record) => show("events", record.id)}
        emptyMessage="No events recorded yet."
      />
      <EmitEventDialog open={emitOpen} onOpenChange={setEmitOpen} />
    </div>
  );
}

export default EventList;
